/** Costeo interno del proyecto — SOLO administración. Planilla detallada, costo por elemento y resumen por categoría. */
import React, { useState, useEffect } from 'react';
import api from '@/api/axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CaretDown, CaretRight, LockKey } from '@phosphor-icons/react';

const Q = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const CAT_LABEL = {
  perfiles: 'Perfiles', vidrio: 'Vidrio', herrajes: 'Herrajes', accesorios: 'Accesorios',
  consumibles: 'Consumibles', mano_obra: 'Mano de obra', materiales: 'Materiales',
  logistica: 'Logística', depreciacion: 'Depreciación', gasolina: 'Gasolina',
  hotel: 'Hotel', alimentacion: 'Alimentación', otros: 'Otros',
};

const ORDEN_CAT = ['perfiles', 'vidrio', 'herrajes', 'accesorios', 'consumibles', 'materiales',
  'mano_obra', 'logistica', 'depreciacion', 'gasolina', 'hotel', 'alimentacion', 'otros'];

export const CosteoInterno = ({ proyectoId, refreshKey }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get(`/sa/proyectos/${proyectoId}/costeo`).then((r) => setData(r.data)).catch(() => {});
  }, [open, proyectoId, refreshKey]);

  const t = data?.totales || {};
  const cats = data?.por_categoria || {};
  const catsOrdenadas = [...ORDEN_CAT.filter((c) => cats[c]), ...Object.keys(cats).filter((c) => !ORDEN_CAT.includes(c))];

  return (
    <Card className="p-0 overflow-hidden border-amber-500/40" data-testid="costeo-interno-card">
      <button type="button" className="w-full flex items-center justify-between px-4 py-3 text-left"
        onClick={() => setOpen((o) => !o)} data-testid="costeo-toggle">
        <span className="text-sm font-black text-amber-500 flex items-center gap-2">
          <LockKey size={16} weight="duotone" /> COSTEO INTERNO — solo administración
        </span>
        {open ? <CaretDown size={16} className="text-amber-500" /> : <CaretRight size={16} className="text-amber-500" />}
      </button>
      {open && !data && <p className="px-4 pb-4 text-sm text-[var(--text-muted)]">Cargando…</p>}
      {open && data && (
        <div className="px-4 pb-4 space-y-4">
          {/* PLANILLA DETALLADA */}
          <div>
            <p className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)] mb-1">Planilla de costos detallados</p>
            {(data.filas || []).length === 0 ? (
              <p className="text-xs text-[var(--text-muted)] italic">Sin líneas de costo todavía.</p>
            ) : (
              <div className="border border-[var(--border)] rounded-lg overflow-x-auto">
                <table className="w-full text-[11px]" data-testid="costeo-planilla">
                  <thead>
                    <tr className="bg-[var(--bg)] text-[var(--text-muted)] text-left uppercase text-[9px]">
                      <th className="px-1.5 py-1.5">Ítem</th>
                      <th className="px-1.5 py-1.5">Elemento</th>
                      <th className="px-1.5 py-1.5">Categoría</th>
                      <th className="px-1.5 py-1.5">Código</th>
                      <th className="px-1.5 py-1.5">Descripción</th>
                      <th className="px-1.5 py-1.5">Unidad</th>
                      <th className="px-1.5 py-1.5 text-right">Cant.</th>
                      <th className="px-1.5 py-1.5 text-right">Costo unit.</th>
                      <th className="px-1.5 py-1.5 text-right">Merma %</th>
                      <th className="px-1.5 py-1.5 text-right">Costo merma</th>
                      <th className="px-1.5 py-1.5 text-right">Costo total</th>
                      <th className="px-1.5 py-1.5">Origen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.filas.map((f, i) => (
                      <tr key={i} className="border-t border-[var(--border)]" data-testid={`costeo-fila-${i}`}>
                        <td className="px-1.5 py-1 font-bold text-[#0EA5E9]">{f.item}</td>
                        <td className="px-1.5 py-1 font-semibold">{f.elemento}</td>
                        <td className="px-1.5 py-1">{CAT_LABEL[f.categoria] || f.categoria}</td>
                        <td className="px-1.5 py-1 text-[#0EA5E9]">{f.codigo || '—'}</td>
                        <td className="px-1.5 py-1">{f.descripcion}{f.error ? <span className="text-amber-500 ml-1">⚠</span> : null}</td>
                        <td className="px-1.5 py-1">{f.unidad || '—'}</td>
                        <td className="px-1.5 py-1 text-right">{f.cantidad ?? '—'}</td>
                        <td className="px-1.5 py-1 text-right">{f.costo_unitario != null ? Q(f.costo_unitario) : '—'}</td>
                        <td className="px-1.5 py-1 text-right">{f.merma_pct ? `${f.merma_pct}%` : '—'}</td>
                        <td className="px-1.5 py-1 text-right">{f.costo_merma ? Q(f.costo_merma) : '—'}</td>
                        <td className="px-1.5 py-1 text-right font-bold">{Q(f.costo_total)}</td>
                        <td className="px-1.5 py-1 text-[var(--text-muted)]">{f.origen}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* COSTO POR ELEMENTO */}
          <div>
            <p className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)] mb-1">Costo por elemento</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="costeo-por-elemento">
              {(data.por_elemento || []).map((e) => (
                <div key={e.codigo} className="border border-[var(--border)] rounded-lg p-2 text-xs">
                  <p className="font-bold"><span className="text-[#0EA5E9]">{e.item} · {e.codigo}</span> {e.nombre} ×{e.cantidad}</p>
                  {Object.entries(e.costos_por_categoria || {}).map(([k, v]) => (
                    <p key={k} className="flex justify-between text-[var(--text-muted)]"><span>{CAT_LABEL[k] || k}</span><span>{Q(v)}</span></p>
                  ))}
                  <p className="flex justify-between font-bold border-t border-[var(--border)] mt-1 pt-1">
                    <span>COSTO {e.codigo}</span><span>{Q(e.costo_total)}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* RESUMEN POR CATEGORÍA + MOTOR FINANCIERO */}
          <div className="border border-amber-500/30 rounded-lg p-3 bg-amber-500/5 max-w-md" data-testid="costeo-resumen">
            <p className="text-xs font-black uppercase tracking-wide text-amber-500 mb-1.5">Resumen interno</p>
            <div className="text-xs space-y-0.5">
              {catsOrdenadas.map((c) => (
                <p key={c} className="flex justify-between text-[var(--text-muted)]" data-testid={`costeo-cat-${c}`}>
                  <span>{CAT_LABEL[c] || c}</span><span>{Q(cats[c])}</span>
                </p>
              ))}
              <p className="flex justify-between font-black border-t border-[var(--border)] pt-1 mt-1">
                <span>COSTO TOTAL</span><span data-testid="costeo-costo-total">{Q(t.costo_total)}</span>
              </p>
              <p className="flex justify-between"><span>Utilidad</span><span>{Q(t.utilidad)}</span></p>
              <p className="flex justify-between text-[var(--text-muted)]">
                <span>{data.metodo_utilidad === 'margen' ? 'Margen' : 'Markup'} configurado</span><span>{data.utilidad_pct}%</span>
              </p>
              <p className="flex justify-between text-[var(--text-muted)]"><span>Margen real / Markup real</span><span>{t.margen_real_pct}% / {t.markup_real_pct}%</span></p>
              <p className="flex justify-between font-semibold border-t border-[var(--border)] pt-1 mt-1"><span>Precio base</span><span>{Q(t.precio_base)}</span></p>
              <p className="flex justify-between"><span>Descuento</span><span>− {Q(t.descuento_monto)}</span></p>
              <p className="flex justify-between"><span>IVA {t.iva_pct || 0}%</span><span>{Q(t.iva_monto)}</span></p>
              <p className="flex justify-between font-black text-[#0EA5E9]"><span>TOTAL</span><span>{Q(t.total)}</span></p>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
